import {
  cloneElement,
  CSSProperties,
  ReactElement,
  ReactNode,
  useContext,
  useState,
  useEffect
} from 'react';
import Image from 'next/image';
import useTranslation from 'next-translate/useTranslation';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useRouter } from 'next/router';
import { Dropdown } from 'antd';
import { UserOutlined } from '@ant-design/icons';

import { UserContext } from '@providers/UserProvider/UserProvider';
import { OPENED_ACCOUNT_MENU } from '@utils/analytics_constants';
import { attachTrackingAnalytics } from '@services/SegmentService';
import { UserInfoDisplay } from '@components/layout/Header/SettingsDisplay/UserInfoDisplay';

import { handleOptionClick } from './handleOptionClick';
import { getDropdownOptions } from './getDropdownOptions';
import style from './UserDropdown.module.scss';

// TODO: we need to adjust ADS to resolve the import issue `Cannot use namespace 'MenuProps' as a type.`
import type { MenuProps } from 'antd';

/**
 * LaunchDarkly Flags: showOrganizationSettings, enablePersonalAccessTokenFrontend
 */
export const UserDropdown = () => {
  const router = useRouter();
  const { t } = useTranslation('common');
  const { showOrganizationSettings, enablePersonalAccessTokenFrontend } = useFlags();
  const user = useContext(UserContext);
  const [favoriteUsers, setFavoriteUsers] = useState({});
  const [userAvatarUrl, setUserAvatarUrl] = useState(undefined);
  const avatarSize = 40;

  useEffect(() => {
    const previousFavoriteUsers = window.localStorage.getItem('FavoriteUsers') || '';
    const mappedFavoriteUsers = Object.fromEntries(
      previousFavoriteUsers
        .split(',')
        .map(fav => fav.split('&'))
        .filter(fav => !!fav[0] && !!fav[1])
    ) || {};

    setFavoriteUsers(mappedFavoriteUsers);
  }, []);

  useEffect(() => {
    const {
      profile_picture_url: userProfilePictureUrl,
    } = user;

    setUserAvatarUrl(userProfilePictureUrl);
  }, [user]);

  const menuStyle: CSSProperties = {
    boxShadow: 'none',
  };

  const handleClick: MenuProps['onClick'] = ({ key }) => {
    return handleOptionClick({ key, router, t, user });
  };

  const customDropdownRender = (menu: ReactNode) => {
    return (
      <div className={style.dropdownMenuContainer} data-testid='avatar-profile-settings-dropdown-menu'>
        <div className={style.userDisplayMenu}>
          <UserInfoDisplay isInDropdown />
        </div>
        {menu && cloneElement(
          menu as ReactElement<{
            style: CSSProperties;
          }>,
          { style: menuStyle },
        )}
      </div>
    );
  };

  const items: MenuProps['items'] = getDropdownOptions({
    enablePersonalAccessTokenFrontend,
    favoriteUsers,
    showOrganizationSettings,
    t,
    user
  });

  return (
    <div className={style.dropdownWrapper}>
      <div className={style.dropdownContainer}>
        <Dropdown
          // TODO: we need to adjust ADS to allow this attribute before converting to ADS
          dropdownRender={(menu: ReactNode) => customDropdownRender(menu)}
          getPopupContainer={(trigger: HTMLElement) => trigger.parentElement as HTMLElement}
          menu={{ items, onClick: handleClick }}
          onOpenChange={(visible: boolean) => {
            if (visible) attachTrackingAnalytics(OPENED_ACCOUNT_MENU);
          }}
          overlayClassName={`${style.dropdown} standard`}
          placement='bottomRight'
          trigger={['click']}
        >
          <div data-testid='avatar-profile-settings-dropdown'>
            {userAvatarUrl ? (
              <Image
                alt={t('general.your_user_avatar')}
                data-testid='profile-avatar'
                height={avatarSize}
                src={userAvatarUrl}
                style={{ borderRadius: 50 }}
                width={avatarSize}
              />
            ) : (
              <UserOutlined
                data-testid='profile-default-avatar'
                style={{ fontSize: avatarSize }}
              />
            )}
          </div>
        </Dropdown>
      </div>
    </div>
  );
};
